import { useEffect, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Briefcase, Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

export default function CreateJobRole() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // { id, name } or null
    const [name, setName] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchRoles = async () => {
        try {
            const { data } = await api.get("/job-roles");
            setRoles(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setName("");
        setError("");
        setDialogOpen(true);
    };

    const openEdit = (role) => {
        setEditing(role);
        setName(role.name);
        setError("");
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setName("");
        setError("");
        setEditing(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Job role name is required.");
            return;
        }
        // Client-side duplicate check
        const dup = roles.some(
            (r) =>
                r.name.toLowerCase() === trimmed.toLowerCase() &&
                r.id !== editing?.id,
        );
        if (dup) {
            setError("A job role with this name already exists.");
            return;
        }
        setSubmitting(true);
        try {
            if (editing) {
                const { data } = await api.put(
                    `/job-roles/${editing.id}`,
                    { name: trimmed },
                );
                setRoles((rs) =>
                    rs.map((r) => (r.id === editing.id ? data : r)),
                );
                toast.success("Job role updated.");
            } else {
                const { data } = await api.post("/job-roles", {
                    name: trimmed,
                });
                setRoles((rs) => [data, ...rs]);
                toast.success("Job role created.");
            }
            closeDialog();
        } catch (err) {
            const msg = apiErrorMessage(err);
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (role) => {
        if (
            !window.confirm(
                `Delete "${role.name}"? This cannot be undone.`,
            )
        )
            return;
        try {
            await api.delete(`/job-roles/${role.id}`);
            setRoles((rs) => rs.filter((r) => r.id !== role.id));
            toast.success("Job role deleted.");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    return (
        <div data-testid="admin-job-role-page">
            <AdminPageHeader
                eyebrow="Modules / Job Roles"
                title="Available Job Roles"
                description="Every role BluBridge hires for. Add, edit or remove roles from here."
                actions={
                    <button
                        onClick={openCreate}
                        data-testid="job-role-add-button"
                        className="inline-flex items-center gap-2 px-5 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                    >
                        <Plus size={14} /> Add Job Role
                    </button>
                }
            />

            <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
                {loading ? (
                    <div
                        data-testid="job-role-loading"
                        className="flex items-center gap-3 text-sm text-[#4B5563]"
                    >
                        <Loader2 size={16} className="animate-spin" />
                        Loading roles…
                    </div>
                ) : roles.length === 0 ? (
                    <div
                        data-testid="job-role-empty"
                        className="bg-white border border-dashed border-[#0F1F38]/20 py-20 px-8 text-center flex flex-col items-center"
                    >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10 mb-6">
                            <Briefcase
                                size={20}
                                strokeWidth={1.5}
                                className="text-[#0F1F38]"
                            />
                        </div>
                        <div className="font-display text-3xl leading-tight text-[#0F1F38]">
                            No job roles yet.
                        </div>
                        <p className="mt-3 max-w-md text-sm text-[#4B5563]">
                            Create your first job role to start building
                            descriptions and application forms around it.
                        </p>
                        <button
                            onClick={openCreate}
                            data-testid="job-role-add-empty"
                            className="mt-8 inline-flex items-center gap-2 px-5 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                        >
                            <Plus size={14} /> Add Job Role
                        </button>
                    </div>
                ) : (
                    <div
                        data-testid="job-role-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {roles.map((role) => (
                            <div
                                key={role.id}
                                data-testid={`job-role-card-${role.id}`}
                                className="bg-white border border-[#0F1F38]/10 p-6 flex flex-col gap-6 hover:border-[#0F1F38]/40 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="w-10 h-10 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10">
                                        <Briefcase
                                            size={16}
                                            strokeWidth={1.5}
                                            className="text-[#0F1F38]"
                                        />
                                    </div>
                                </div>
                                <div className="font-display text-2xl leading-tight text-[#0F1F38] break-words">
                                    {role.name}
                                </div>
                                <div className="mt-auto flex items-center gap-2 pt-2">
                                    <button
                                        onClick={() => openEdit(role)}
                                        data-testid={`job-role-edit-${role.id}`}
                                        className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Pencil size={12} /> Edit
                                    </button>
                                    <button
                                        onClick={() => onDelete(role)}
                                        data-testid={`job-role-delete-${role.id}`}
                                        className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-red-700 hover:text-red-700 text-xs tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog
                open={dialogOpen}
                onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}
            >
                <DialogContent
                    data-testid="job-role-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-md"
                >
                    <DialogHeader>
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            {editing ? "Edit role" : "New role"}
                        </div>
                        <DialogTitle className="font-display text-3xl leading-tight text-[#0F1F38]">
                            {editing ? "Edit Job Role" : "Add Job Role"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5563]">
                            {editing
                                ? "Rename this job role. Existing descriptions and forms stay linked."
                                : "Give this role a short, distinctive name."}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <Label
                                htmlFor="job-role-name"
                                className="text-[10px] tracking-label uppercase text-[#0F1F38]/60"
                            >
                                Job Role Name
                            </Label>
                            <Input
                                id="job-role-name"
                                data-testid="job-role-name-input"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (error) setError("");
                                }}
                                placeholder="e.g. Senior Backend Engineer"
                                autoFocus
                                className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white focus-visible:ring-[#0F1F38] focus-visible:ring-offset-0"
                            />
                            {error && (
                                <div
                                    data-testid="job-role-dialog-error"
                                    className="mt-3 text-sm text-red-700 border-l-2 border-red-700 pl-3"
                                >
                                    {error}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={closeDialog}
                                data-testid="job-role-dialog-cancel"
                                className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="job-role-dialog-submit"
                                disabled={submitting}
                                className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {submitting && (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                )}
                                {editing ? "Save" : "Create"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
